/**
 * ComponentWeakRegistry
 *
 * Tracks live component instances via WeakRef. When the JS engine GCs an
 * instance that was never explicitly unregistered, a FinalizationRegistry
 * callback fires to run emergency cleanup (stop leaked effects, release
 * global keys, etc.).
 *
 * Two paths:
 *   - Explicit (preferred): the renderer calls `unregister(key)` during normal
 *     unmount. Cleanup runs synchronously, the entry is removed, and the
 *     finalizer is detached.
 *   - GC-triggered (fallback): if a reference is dropped without calling
 *     `unregister` (a bug, but we want to recover gracefully), the
 *     FinalizationRegistry fires the callback asynchronously after the
 *     instance is collected.
 *
 * Browser support: WeakRef + FinalizationRegistry available in
 * Chrome 84+, Firefox 79+, Safari 14.1+ (already required by flexium's
 * existing Sprint 2 globalRegistry work).
 */

interface Entry {
    ref: WeakRef<object>
    cleanup: () => void
    /** FinalizationRegistry unregister token — opaque object identity. */
    token: object
}

export class ComponentWeakRegistry {
    private readonly _entries = new Map<string, Entry>()
    private readonly _finalizer: FinalizationRegistry<string>

    constructor() {
        this._finalizer = new FinalizationRegistry<string>((key) => {
            const entry = this._entries.get(key)
            if (!entry) return
            // GC-triggered: instance was collected without explicit unregister.
            // Run emergency cleanup, then drop the entry.
            entry.cleanup()
            this._entries.delete(key)
        })
    }

    get size(): number {
        return this._entries.size
    }

    has(key: string): boolean {
        return this._entries.has(key)
    }

    /**
     * Register `instance` under `key`.
     *
     * `cleanup` is called either via explicit `unregister(key)` or via the
     * FinalizationRegistry when `instance` is GC'd.
     *
     * Re-registering an existing key first unregisters the previous binding
     * (its cleanup fires immediately), so callers don't have to worry about
     * stale entries when a component re-mounts under the same key.
     */
    register(instance: object, key: string, cleanup: () => void): void {
        if (this._entries.has(key)) this.unregister(key)
        const token = {}
        const entry: Entry = {
            ref: new WeakRef(instance),
            cleanup,
            token,
        }
        this._entries.set(key, entry)
        this._finalizer.register(instance, key, token)
    }

    /**
     * Explicitly unregister and run cleanup synchronously.
     * Idempotent — safe to call multiple times for the same key.
     */
    unregister(key: string): void {
        const entry = this._entries.get(key)
        if (!entry) return
        this._finalizer.unregister(entry.token)
        this._entries.delete(key)
        entry.cleanup()
    }

    /**
     * Return the WeakRef for `key`, or null if not registered.
     * Callers should `.deref()` to obtain the instance (may be null if the
     * underlying object was collected between `getRef` and `deref`).
     */
    getRef(key: string): WeakRef<object> | null {
        return this._entries.get(key)?.ref ?? null
    }

    /** Reset all registrations without running cleanups — test helper only. */
    __resetForTest(): void {
        this._entries.clear()
    }
}

/**
 * Module-level singleton. The renderer registers/unregisters component
 * instances against this. Tests can call `__resetForTest()` for determinism.
 */
export const componentRegistry = new ComponentWeakRegistry()
